import { Button, ExclusiveChoiceDropdown, ModalPanel, Title } from "dash-styled"
import React, { FC, FormEvent, useCallback, useMemo, useState } from "react"
import {
    APIWorker,
    getWorkerDisplayText
} from "../../../../../../Models/API/Worker"
import { APIWorkerGroup } from "../../../../../../Models/API/WorkerGroup"
import { ReactComponent as IconClose } from "icons/lib/close-icon.svg"

interface WorkerGroupModifyModalProps {
    workers: APIWorker[]
    group: APIWorkerGroup
    isSaving: boolean
    onClose: () => void
    onSubmit: (group: APIWorkerGroup) => void
    onLoadMoreWorkers: () => void
}

export const WorkerGroupModifyModal: FC<WorkerGroupModifyModalProps> = ({
    workers,
    group,
    isSaving,
    onClose,
    onSubmit,
    onLoadMoreWorkers
}) => {
    const [addedWorkers, setAddedWorkers] = useState<APIWorker[]>(group.workers)
    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault()

            onSubmit({ ...group, workers: addedWorkers })
        },
        [group, onSubmit, addedWorkers]
    )
    const handleAdd = useCallback(
        (worker: APIWorker) => setAddedWorkers(a => a.concat([worker])),
        []
    )
    const workersToAdd = useMemo(
        () => workers.filter(w => addedWorkers.every(a => a.id !== w.id)),
        [addedWorkers, workers]
    )

    return (
        <ModalPanel onClose={onClose} className="max-w-2xl w-full">
            <Title variant="heading2" weight="semibold" component="h2">
                Modify workers
            </Title>
            <form onSubmit={handleSubmit}>
                <ExclusiveChoiceDropdown
                    label="Workers"
                    placeholder="Select worker to add"
                    choices={workersToAdd}
                    value={undefined}
                    emptyText="Please add at a Worker first"
                    renderChoice={getWorkerDisplayText}
                    onClick={handleAdd}
                    onReachedToEndOfChoices={onLoadMoreWorkers}
                />
                {addedWorkers.length > 0 && (
                    <ul className="max-h-40vh overflow-auto mt-2 border-0 border-t border-b border-solid border-rhino-300 divide-y divide-solid divide-rhino-300">
                        {addedWorkers.map(worker => (
                            <li
                                key={worker.id}
                                className="py-3 px-2 flex items-center justify-between"
                            >
                                {getWorkerDisplayText(worker)}
                                <button
                                    type="button"
                                    className="text-smoke-350 align-middle"
                                    onClick={() =>
                                        setAddedWorkers(current =>
                                            current.filter(
                                                w => w.id !== worker.id
                                            )
                                        )
                                    }
                                >
                                    <IconClose className="w-4 h-4" />
                                    <span className="sr-only">Delete</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <Button
                    type="submit"
                    className="w-auto mt-8 px-8"
                    disabled={isSaving}
                >
                    {isSaving ? "Updating..." : "Update"}
                </Button>
            </form>
        </ModalPanel>
    )
}
